import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Sidebar from './components/Sidebar';
import Chat from './components/Chat';
import Preview from './components/Preview';
import ConversationPanel from './components/ConversationPanel';
import AuthPage from './components/AuthPage';
import { listFiles, getServeUrl, getMe, getAuthToken, clearAuthToken } from './api';

const STORAGE_KEY = 'argon-kb-conversations';
const ACTIVE_KEY = 'argon-kb-active-id';

function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function loadConversations() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveConversations(convs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(convs));
  } catch { /* quota exceeded, ignore */ }
}

function loadActiveId() {
  try {
    return localStorage.getItem(ACTIVE_KEY);
  } catch {
    return null;
  }
}

function saveActiveId(id) {
  try {
    if (id) localStorage.setItem(ACTIVE_KEY, id);
    else localStorage.removeItem(ACTIVE_KEY);
  } catch { /* ignore */ }
}

export default function App() {
  // ── Auth state ──
  const [authToken, setAuthTokenState] = useState(getAuthToken);
  const [currentUser, setCurrentUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // ── App state ──
  const [conversations, setConversationsRaw] = useState(loadConversations);
  const conversationsRef = useRef(null);
  if (conversationsRef.current === null) {
    conversationsRef.current = loadConversations();
  }
  const [activeId, setActiveId] = useState(loadActiveId);
  const activeIdRef = useRef(null);
  if (activeIdRef.current === null) {
    activeIdRef.current = loadActiveId();
  }
  const [files, setFiles] = useState([]);

  function setConversations(value) {
    if (typeof value === 'function') {
      setConversationsRaw(prev => {
        const next = value(prev);
        conversationsRef.current = next;
        return next;
      });
    } else {
      conversationsRef.current = value;
      setConversationsRaw(value);
    }
  }
  const [selectedFile, setSelectedFile] = useState(null);
  const [serveUrl, setServeUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [viewMode, setViewMode] = useState('chat');
  const [showConversations, setShowConversations] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const activeConv = conversations.find(c => c.id === activeId) || null;
  const messages = activeConv?.messages || [];
  const hasStarted = conversations.length > 0 && activeId !== null;

  // Persist conversations
  useEffect(() => { saveConversations(conversations); }, [conversations]);
  useEffect(() => {
    saveActiveId(activeId);
    activeIdRef.current = activeId;
  }, [activeId]);

  // Check auth on mount
  useEffect(() => {
    async function checkAuth() {
      const token = getAuthToken();
      if (!token) {
        setAuthChecked(true);
        return;
      }
      try {
        const res = await getMe();
        setCurrentUser(res.data);
      } catch {
        clearAuthToken();
      }
      setAuthChecked(true);
    }
    checkAuth();
  }, []);

  // Load files when authenticated
  useEffect(() => {
    if (authToken) {
      loadFiles();
    }
  }, [authToken]);

  const loadFiles = async () => {
    try {
      const res = await listFiles();
      setFiles(res.data);
    } catch (e) {
      console.error('Failed to load files', e);
    }
  };

  // ── Auth handlers ──

  const handleLogin = useCallback((data) => {
    setAuthTokenState(getAuthToken());
    setCurrentUser(data);
    setAuthChecked(true);
  }, []);

  const handleLogout = useCallback(() => {
    clearAuthToken();
    setAuthTokenState(null);
    setCurrentUser(null);
    setConversations([]);
    conversationsRef.current = [];
    setActiveId(null);
    activeIdRef.current = null;
  }, []);

  // ── Conversation actions ──

  const handleNewConversation = useCallback(() => {
    const id = genId();
    const now = Date.now();
    const conv = { id, title: '', messages: [], createdAt: now, updatedAt: now };
    setConversations(prev => [conv, ...prev]);
    setActiveId(id);
    activeIdRef.current = id;
    setViewMode('chat');
    setShowConversations(false);
  }, []);

  const handleSelectConversation = useCallback((id) => {
    setActiveId(id);
    activeIdRef.current = id;
    setViewMode('chat');
    setShowConversations(false);
  }, []);

  const handleDeleteConversation = useCallback((id) => {
    setConversations(prev => {
      const next = prev.filter(c => c.id !== id);
      if (id === activeIdRef.current) {
        const newActive = next.length > 0 ? next[0].id : null;
        setActiveId(newActive);
        activeIdRef.current = newActive;
        if (!newActive) setViewMode('chat');
      }
      return next;
    });
  }, []);

  const handleTogglePanel = useCallback(() => {
    setShowConversations(prev => !prev);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => !prev);
  }, []);

  // ── Message actions on active conversation ──

  const handleSendMessage = useCallback((msg) => {
    const aid = activeIdRef.current;
    const convExists = aid && conversationsRef.current.find(c => c.id === aid);

    if (!convExists) {
      if (msg.role !== 'user') return;
      const id = genId();
      const now = Date.now();
      const title = msg.content.slice(0, 50);
      const conv = { id, title, messages: [msg], createdAt: now, updatedAt: now };
      setConversations(prev => [conv, ...prev]);
      setActiveId(id);
      activeIdRef.current = id;
      return;
    }

    setConversations(prev => prev.map(c => {
      if (c.id !== aid) return c;
      const title = c.title || (msg.role === 'user' ? msg.content.slice(0, 50) : c.title);
      return {
        ...c,
        title,
        messages: [...c.messages, msg],
        updatedAt: Date.now(),
      };
    }));
  }, []);

  const handleUpdateLastMessage = useCallback((updater) => {
    const aid = activeIdRef.current;
    if (!aid) return;

    setConversations(prev => prev.map(c => {
      if (c.id !== aid) return c;
      const msgs = [...c.messages];
      if (msgs.length === 0) return c;
      const last = msgs[msgs.length - 1];
      if (last.role !== 'assistant') return c;
      const updated = typeof updater === 'function' ? updater(last) : { ...last, ...updater };
      msgs[msgs.length - 1] = updated;
      return { ...c, messages: msgs, updatedAt: Date.now() };
    }));
  }, []);

  // ── File actions ──

  const handleSelectFile = (file) => {
    setSelectedFile(file);
    setServeUrl(getServeUrl(file.base_name));
    setViewMode('preview');
  };

  const handleBackToChat = () => {
    setViewMode('chat');
  };

  const handleUpload = async () => {
    await loadFiles();
  };

  const handleDelete = async (baseName) => {
    await loadFiles();
    if (selectedFile?.base_name === baseName) {
      setSelectedFile(null);
      setServeUrl(null);
      setViewMode('chat');
    }
  };

  // ── Show loading while checking auth ──
  if (!authChecked) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-gray-50">
        <div className="w-2 h-2 bg-gray-400 rounded-full animate-pulse" />
      </div>
    );
  }

  // ── Not authenticated → show auth page ──
  if (!authToken) {
    return <AuthPage onLogin={handleLogin} />;
  }

  // ── Authenticated → main app ──
  return (
    <div className="flex h-screen w-screen overflow-hidden bg-gray-50 text-gray-900">
      {/* Sidebar */}
      <div className="relative flex-shrink-0">
        <Sidebar
          files={files}
          selectedFile={selectedFile}
          onSelectFile={handleSelectFile}
          onBackToChat={handleBackToChat}
          onUpload={handleUpload}
          onDelete={handleDelete}
          uploading={uploading}
          setUploading={setUploading}
          viewMode={viewMode}
          collapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          currentUser={currentUser}
          onLogout={handleLogout}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden relative">
        <AnimatePresence mode="wait">
          {viewMode === 'preview' && selectedFile ? (
            <motion.div
              key="preview"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.25 }}
              className="flex-1 flex overflow-hidden"
            >
              <Preview file={selectedFile} serveUrl={serveUrl} onBack={handleBackToChat} />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              className="flex-1 flex overflow-hidden"
            >
              <Chat
                messages={messages}
                onSendMessage={handleSendMessage}
                onUpdateLastMessage={handleUpdateLastMessage}
                hasStarted={hasStarted}
                selectedFile={selectedFile}
                onUpload={handleUpload}
                onNewConversation={handleNewConversation}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Conversation Panel (right side) */}
        <ConversationPanel
          conversations={conversations}
          activeId={activeId}
          onSelect={handleSelectConversation}
          onNew={handleNewConversation}
          onDelete={handleDeleteConversation}
          open={showConversations}
          onToggle={handleTogglePanel}
        />
      </div>
    </div>
  );
}
